import React, { createContext, useContext, useState, useEffect } from 'react';
import dataService from '../services/dataService';
import { log as debugLog, error as logError } from '../utils/logger';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Idioma por defecto: inglés. Cargar de sessionStorage primero, luego de preferencias del usuario
  const getInitialLanguage = () => {
    // Primero intentar sessionStorage (para usuarios sin login)
    const sessionLang = sessionStorage.getItem('language');
    if (sessionLang === 'es' || sessionLang === 'en') {
      return sessionLang;
    }
    // Si no hay en sessionStorage, intentar localStorage (persistencia entre sesiones)
    const localLang = localStorage.getItem('language');
    if (localLang === 'es' || localLang === 'en') {
      return localLang;
    }
    // Por defecto: inglés
    return 'en';
  };

  const [language, setLanguage] = useState(getInitialLanguage);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLanguagePreference();
  }, []);

  const loadLanguagePreference = async () => {
    try {
      // Primero verificar sessionStorage/localStorage
      const storedLang = sessionStorage.getItem('language') || localStorage.getItem('language');
      if (storedLang === 'es' || storedLang === 'en') {
        setLanguage(storedLang);
        setLoading(false);
        return;
      }

      // Si hay usuario logueado, intentar cargar de preferencias
      try {
        const preferences = await dataService.getUserPreferences();
        if (preferences?.language && (preferences.language === 'es' || preferences.language === 'en')) {
          setLanguage(preferences.language);
          // Guardar también en sessionStorage para consistencia
          sessionStorage.setItem('language', preferences.language);
          localStorage.setItem('language', preferences.language);
        }
      } catch (err) {
        // Si no hay usuario o falla, usar el idioma por defecto
        debugLog('No user preferences available, using default language');
      }
    } catch (error) {
      logError('Error loading language preference:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = async (lang) => {
    if (lang !== 'es' && lang !== 'en') return;
    
    setLanguage(lang);
    // Guardar en sessionStorage y localStorage inmediatamente
    sessionStorage.setItem('language', lang);
    localStorage.setItem('language', lang);
    
    // Si hay usuario logueado, también guardar en preferencias
    try {
      const preferences = await dataService.getUserPreferences();
      if (preferences) {
        await dataService.updateUserPreferences({
          ...preferences,
          language: lang
        });
      }
    } catch (error) {
      // Si no hay usuario o falla, no es crítico, ya está guardado en sessionStorage
      debugLog('Could not save language to user preferences:', error);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, loading }}>
      {children}
    </LanguageContext.Provider>
  );
};
